import { useEffect, useState } from "react";

// First-party analytics consent. Landing analytics (useLandingAnalytics) run
// under the first-party audience-measurement basis: they are allowed BY DEFAULT
// (no choice yet) and only stop when the visitor explicitly opts out ("denied").
// A soft notice + a "Cookie preferences" control let anyone opt out in one step;
// the choice is stored so the notice does not reappear.

const CONSENT_KEY = "adam:analytics_consent";
const CONSENT_EVENT = "adam:analytics-consent-changed";

export type AnalyticsConsent = "granted" | "denied";

export function getAnalyticsConsent(): AnalyticsConsent | null {
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    return v === "granted" || v === "denied" ? v : null;
  } catch {
    return null;
  }
}

/** Allowed unless the visitor explicitly opted out. Default (no choice) and an
 * explicit opt-in both allow; only "denied" turns analytics off. */
export function analyticsAllowed(): boolean {
  return getAnalyticsConsent() !== "denied";
}

export function setAnalyticsConsent(value: AnalyticsConsent): void {
  try {
    localStorage.setItem(CONSENT_KEY, value);
  } catch {
    // localStorage unavailable (private mode) — consent is in-memory only for
    // this tab; dispatch still lets the banner + analytics react immediately.
  }
  try {
    window.dispatchEvent(
      new CustomEvent<AnalyticsConsent>(CONSENT_EVENT, { detail: value })
    );
  } catch {
    /* non-browser env */
  }
}

/**
 * Reactive consent state. Re-renders when consent changes in this tab (custom
 * event) or another tab (storage event), so the banner hides and the analytics
 * hooks start/stop the moment the choice is made.
 */
export function useAnalyticsConsent(): {
  consent: AnalyticsConsent | null;
  grant: () => void;
  deny: () => void;
  optIn: () => void;
  optOut: () => void;
} {
  const [consent, setConsent] = useState<AnalyticsConsent | null>(() =>
    getAnalyticsConsent()
  );

  useEffect(() => {
    const sync = () => setConsent(getAnalyticsConsent());
    window.addEventListener(CONSENT_EVENT, sync as EventListener);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(CONSENT_EVENT, sync as EventListener);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const grant = () => setAnalyticsConsent("granted");
  const deny = () => setAnalyticsConsent("denied");
  // optIn/optOut are semantic aliases used by the preferences UI.
  return { consent, grant, deny, optIn: grant, optOut: deny };
}
